import { useSnackbar } from 'notistack'
import { FC, useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import {
  CancellationPolicySaveWarningModal,
  CancellationPolicySettingsSection,
} from 'src/modules/cancellation-policy/components'
import { Button, FormattedText } from 'src/modules/core/components'
import { AccountCountryCard } from 'src/modules/user/components/account-country-card'
import { ClientMessageLanguageCard } from 'src/modules/user/components/client-message-language-card'
import { SettingsEmailNotificationsCard } from 'src/modules/user/components/settings-email-notifications-card'
import { SettingsProfileInfoFormCustomerMode } from 'src/modules/user/components/settings-profile-info-form/settings-profile-info-form-customer-mode'
import { SettingsSetAndForgetRemindersCard } from 'src/modules/user/components/settings-set-and-forget-reminders-card'
import { useBookingOnboarding } from 'src/modules/user/hooks/use-booking-onboarding'
import type { ProfileFormLiftedValues } from '../settings-profile-info-form/settings-profile-info-form.interface'
import formClasses from '../settings-profile-info-form/settings-profile-info-form.module.scss'
import type {
  OtherSettingsAutoActivateFormHandle,
  OtherSettingsCancellationPolicyFormHandle,
  OtherSettingsNotificationPreferencesFormHandle,
  OtherSettingsProfileFormHandle,
} from './other-settings-form-handles'
import type { SettingsProfileInfoProps } from './settings-profile-info.interface'
import classes from './settings-profile-info.module.scss'

export const SettingsProfileInfoCustomerMode: FC<SettingsProfileInfoProps> = ({ profileInfo, preferences }) => {
  const [formUseEventTimeZone, setFormUseEventTimeZone] = useState<boolean | null>(null)
  const [liftedProfileValues, setLiftedProfileValues] = useState<ProfileFormLiftedValues | null>(null)
  const [profileSectionDirty, setProfileSectionDirty] = useState(false)
  const [notificationsSectionDirty, setNotificationsSectionDirty] = useState(false)
  const [autoActivateSectionDirty, setAutoActivateSectionDirty] = useState(false)
  const [policySectionDirty, setPolicySectionDirty] = useState(false)
  const [isSavingAll, setIsSavingAll] = useState(false)
  const [isPolicySaveWarningOpen, setIsPolicySaveWarningOpen] = useState(false)

  const profileFormRef = useRef<OtherSettingsProfileFormHandle>(null)
  const notificationsFormRef = useRef<OtherSettingsNotificationPreferencesFormHandle>(null)
  const autoActivateFormRef = useRef<OtherSettingsAutoActivateFormHandle>(null)
  const policyFormRef = useRef<OtherSettingsCancellationPolicyFormHandle>(null)

  const navigate = useNavigate()
  const calendars = useCalendars()
  const { data: bookingOnboarding } = useBookingOnboarding()
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()

  const hasCustomTimezones = useMemo(() => {
    return calendars.data?.some((calendar) => calendar.timezone_custom) || false
  }, [calendars.data])

  const currentUseEventTimeZone =
    formUseEventTimeZone !== null ? formUseEventTimeZone : !!profileInfo?.use_event_timezone

  const handleFormValuesChange = useCallback((values: ProfileFormLiftedValues) => {
    setFormUseEventTimeZone(values.useEventTimeZone)
    setLiftedProfileValues(values)
  }, [])

  const anyDirty = profileSectionDirty || notificationsSectionDirty || autoActivateSectionDirty || policySectionDirty

  const saveAllSections = useCallback(async () => {
    setIsSavingAll(true)
    try {
      const results = await Promise.all([
        profileFormRef.current?.submitIfDirty() ?? Promise.resolve(false),
        notificationsFormRef.current?.submitIfDirty() ?? Promise.resolve(false),
        autoActivateFormRef.current?.submitIfDirty() ?? Promise.resolve(false),
        policyFormRef.current?.submitIfDirty() ?? Promise.resolve(false),
      ])
      if (results.some(Boolean)) {
        enqueueSnackbar(formatMessage({ id: 'settings.saved' }), { variant: 'success' })
      }
    } finally {
      setIsSavingAll(false)
    }
  }, [enqueueSnackbar, formatMessage])

  // The policy is checked before Promise.all: a 400 or a cancelled warning must not leave a partial save.
  const handleSaveAll = useCallback(async () => {
    const policyForm = policyFormRef.current
    if (policyForm && !policyForm.validate()) return
    if (policyForm?.needsConfirmation()) {
      setIsPolicySaveWarningOpen(true)
      return
    }
    await saveAllSections()
  }, [saveAllSections])

  return (
    <>
      <div className={classes.profileInfo}>
        <SettingsProfileInfoFormCustomerMode
          ref={profileFormRef}
          profileInfo={profileInfo}
          preferences={preferences}
          hideSubmitButton
          onDirtyChange={setProfileSectionDirty}
          onFormValuesChange={handleFormValuesChange}
        />
        <CancellationPolicySettingsSection
          ref={policyFormRef}
          userId={profileInfo.id}
          appointmentConfirmationEnabled={
            liftedProfileValues?.appointmentConfirmation ?? !!profileInfo.appointment_confirmation
          }
          reschedulingEnabled={
            liftedProfileValues?.appointmentConfirmationReschedule ?? !!bookingOnboarding?.is_rescheduling_enabled
          }
          onDirtyChange={setPolicySectionDirty}
        />
      </div>

      <SettingsSetAndForgetRemindersCard
        ref={autoActivateFormRef}
        hideSubmitButton
        onDirtyChange={setAutoActivateSectionDirty}
      />

      <SettingsEmailNotificationsCard
        ref={notificationsFormRef}
        hideSubmitButton
        onDirtyChange={setNotificationsSectionDirty}
      />

      <AccountCountryCard />
      <ClientMessageLanguageCard />

      {currentUseEventTimeZone && hasCustomTimezones && (
        <div className={classes.customTimezoneNotice}>
          <span>
            You&apos;ve set{' '}
            <a
              href="/dashboard/settings?tab=calendars"
              className={classes.customTimezoneLink}
              onClick={(e) => {
                e.preventDefault()
                navigate('/dashboard/settings?tab=calendars')
              }}
            >
              custom time zones in one or more calendars
            </a>
            . The app will use the custom time zone settings unless you set a specific time zone in the calendar&apos;s
            event.
          </span>
        </div>
      )}

      <div className={classes.saveAllRow}>
        <Button
          type="button"
          className={formClasses.button}
          widthType="content"
          isLoading={isSavingAll}
          disabled={!anyDirty || isSavingAll}
          onClick={() => void handleSaveAll()}
        >
          <FormattedText id="common.save" />
        </Button>
      </div>

      {isPolicySaveWarningOpen && (
        <CancellationPolicySaveWarningModal
          onCancel={() => setIsPolicySaveWarningOpen(false)}
          onConfirm={() => {
            setIsPolicySaveWarningOpen(false)
            void saveAllSections()
          }}
        />
      )}
    </>
  )
}
